import { supabase } from '../lib/supabase';
import { UserProfile } from './userService';
import logger from '../utils/logger';

// Yardımcı fonksiyonlar
const generateInviteCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// Ekip oluştur
export const createTeam = async (name: string, description: string, ownerId: string) => {
    try {
        const inviteCode = generateInviteCode();
        const { data, error } = await supabase
            .from('teams')
            .insert([{
                name,
                invite_code: inviteCode,
                owner_id: ownerId
            }])
            .select()
            .single();

        if (error) throw error;

        // Kullanıcıyı bu ekibin sahibi yap
        await supabase.from('user_profiles').update({
            team_id: data.id,
            team_role: 'OWNER'
        }).eq('id', ownerId);

        return data;
    } catch (error) {
        logger.error('Ekip oluşturma hatası:', error);
        throw error;
    }
};

// Ekip bilgilerini dinle (onSnapshot -> Realtime Channels)
export const listenTeam = (teamId: string, callback: (team: any) => void) => {
    // Önce mevcut durumu çek
    const fetchTeam = async () => {
        const { data } = await supabase.from('teams').select('*').eq('id', teamId).single();
        if (data) callback(data);
        else callback(null);
    };

    fetchTeam();

    // Değişiklikleri dinle
    const channel = supabase
        .channel(`public:teams:${teamId}`)
        .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'teams', filter: `id=eq.${teamId}` },
            (payload) => {
                callback(payload.new);
            }
        )
        .subscribe();

    // Unsubscribe fonksiyonu
    return () => {
        supabase.removeChannel(channel);
    };
};

// Ekip üyelerini getir
export const getTeamMembers = async (teamId: string): Promise<UserProfile[]> => {
    try {
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('team_id', teamId);

        if (error) throw error;
        return (data || []) as UserProfile[];
    } catch (error) {
        logger.error('Ekip üyeleri alma hatası:', error);
        throw error;
    }
};

// Üye rolünü güncelle
export const updateMemberRole = async (userId: string, teamId: string, role: string) => {
    try {
        const { error } = await supabase
            .from('user_profiles')
            .update({ team_role: role })
            .eq('id', userId)
            .eq('team_id', teamId);

        if (error) throw error;
    } catch (error) {
        logger.error('Üye rol güncelleme hatası:', error);
        throw error;
    }
};

// Ekibe davet kodu ile katıl
export const joinTeamWithCode = async (inviteCode: string, userId: string) => {
    try {
        // Ekibi bul
        const { data: team, error } = await supabase
            .from('teams')
            .select('id, max_members')
            .eq('invite_code', inviteCode)
            .single();

        if (error || !team) {
            throw new Error('Geçersiz ekip kodu!');
        }

        // Mevcut üye sayısını kontrol et
        const { count, error: countError } = await supabase
            .from('user_profiles')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', team.id);

        if (countError) throw countError;

        const maxMembers = team.max_members || 10;
        if (count !== null && count >= maxMembers) {
            throw new Error(`Ekip dolu! Maksimum ${maxMembers} üye alınabilir.`);
        }

        // Kullanıcı profilini güncelle
        const { error: updateError } = await supabase.from('user_profiles').update({
            team_id: team.id,
            team_role: 'MEMBER'
        }).eq('id', userId);

        if (updateError) throw updateError;

        return team;
    } catch (error) {
        logger.error('Ekibe katılma hatası:', error);
        throw error;
    }
};

// Eksik invite_code veya owner_id varsa düzelt
export const ensureTeamSetup = async (teamId: string, userId: string) => {
    try {
        const { data: team } = await supabase.from('teams').select('invite_code, owner_id').eq('id', teamId).single();
        if (!team) return;

        const updates: any = {};
        if (!team.invite_code) {
            updates.invite_code = generateInviteCode();
        }
        if (!team.owner_id) {
            updates.owner_id = userId;
        }

        if (Object.keys(updates).length > 0) {
            await supabase.from('teams').update(updates).eq('id', teamId);
            logger.info('Team setup düzeltildi:', updates);
        }
    } catch (error) {
        logger.error('Team setup düzeltme hatası:', error);
    }
};

// Ekip davet kodunu yenile
export const refreshInviteCode = async (teamId: string, ownerId: string) => {
    try {
        // Güvenlik: İşlemi yapan kişi owner mı? RLS koruması yapsa da frontend check
        const { data: team } = await supabase.from('teams').select('owner_id').eq('id', teamId).single();
        if (team?.owner_id !== ownerId) {
            throw new Error('Bu işlem için yetkiniz yok');
        }

        const newInviteCode = generateInviteCode();

        const { error } = await supabase.from('teams').update({
            invite_code: newInviteCode
        }).eq('id', teamId);

        if (error) throw error;

        return newInviteCode;
    } catch (error) {
        logger.error('Davet kodu yenileme hatası:', error);
        throw error;
    }
};

// Üyeyi ekipten çıkar
export const removeMember = async (teamId: string, memberId: string) => {
    try {
        const { error } = await supabase.from('user_profiles').update({
            team_id: null,
            team_role: null
        }).eq('id', memberId).eq('team_id', teamId);

        if (error) throw error;
    } catch (error) {
        logger.error('Üye çıkarma hatası:', error);
        throw error;
    }
};

// Ekip sahipliğini devret
export const transferOwnership = async (teamId: string, currentOwnerId: string, newOwnerId: string) => {
    try {
        // Güvenlik: İşlemi yapan kişi gerçekten owner mı?
        const { data: team } = await supabase.from('teams').select('owner_id').eq('id', teamId).single();
        if (team?.owner_id !== currentOwnerId) {
            throw new Error('Bu işlem için yetkiniz yok');
        }

        // Yeni sahip aynı ekipte mi?
        const { data: newOwner } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', newOwnerId)
            .eq('team_id', teamId)
            .single();
        if (!newOwner) {
            throw new Error('Seçilen kullanıcı bu ekipte değil');
        }

        // teams tablosunda owner_id güncelle
        const { error: teamError } = await supabase
            .from('teams')
            .update({ owner_id: newOwnerId })
            .eq('id', teamId);
        if (teamError) throw teamError;

        // Eski owner → ADMIN'e düşür
        const { error: oldOwnerError } = await supabase.from('user_profiles')
            .update({ team_role: 'ADMIN' })
            .eq('id', currentOwnerId)
            .eq('team_id', teamId);
        if (oldOwnerError) throw oldOwnerError;

        // Yeni owner → OWNER'a yükselt
        const { error: newOwnerError } = await supabase.from('user_profiles')
            .update({ team_role: 'OWNER' })
            .eq('id', newOwnerId)
            .eq('team_id', teamId);
        if (newOwnerError) throw newOwnerError;
    } catch (error) {
        logger.error('Sahiplik devri hatası:', error);
        throw error;
    }
};

// Ekipten ayrıl (Kullanıcı kendi çıkar)
export const leaveTeam = async (teamId: string, userId: string) => {
    try {
        const { error } = await supabase.from('user_profiles').update({
            team_id: null,
            team_role: null
        }).eq('id', userId).eq('team_id', teamId);

        if (error) throw error;
    } catch (error) {
        logger.error('Ekipten ayrılma hatası:', error);
        throw error;
    }
};
